import { z } from 'zod';
import { TimestampSchema } from '../common/clock.js';
import { AgentCardIdSchema, ApplicationIdSchema, CreditLineIdSchema, PaymentIntentIdSchema, ScaChallengeIdSchema, } from '../common/ids.js';
import { MoneySchema } from '../common/money.js';
import { RateSchema } from '../common/rate.js';
import { EvidenceTypeSchema } from '../catalogs/domain-catalogs.js';
import { CreditLineSummarySchema, SimulationResultSchema } from '../credit/schemas.js';
import { CashFlowAnalysisSchema, DocumentReviewSchema, OfferSchema, } from '../origination/schemas.js';
import { ApplicationPhaseSchema } from '../origination/states.js';
import { PayeeSchema } from '../payments/schemas.js';
import { PaymentIntentStatusSchema } from '../payments/states.js';
import { ReasonCodeSchema } from '../reason-codes.js';
/**
 * Tarjetas de UI que el agente devuelve en el chat (`card.type`). Las cifras dentro de una tarjeta
 * vienen de herramientas determinísticas (credit-math / Credit Core), nunca del texto del LLM.
 * Las acciones que mueven dinero devuelven `scaChallengeId` y se completan vía la API normal.
 */
const base = (type) => z.object({
    id: AgentCardIdSchema,
    type: z.literal(type),
    createdAt: TimestampSchema,
    expiresAt: TimestampSchema.nullable(),
});
export const CardActionSchema = z
    .enum([
    'CONFIRM_PAYMENT_DRAFT',
    'CONFIRM',
    'EDIT',
    'CANCEL',
    'UPLOAD_REQUESTED_DOC',
    'ACCEPT_OFFER',
    'CONFIRM_FACTS',
    'SAVE_NICKNAME',
])
    .meta({ id: 'CardAction' });
export const PayeeConfirmationCardSchema = base('PAYEE_CONFIRMATION').extend({
    payee: PayeeSchema,
    nickname: z.string().nullable(),
    actions: z.array(CardActionSchema),
});
export const PaymentDraftCardSchema = base('PAYMENT_DRAFT').extend({
    paymentIntentId: PaymentIntentIdSchema,
    payee: PayeeSchema,
    amount: MoneySchema,
    installments: z.int(),
    simulation: SimulationResultSchema,
    scaChallengeId: ScaChallengeIdSchema.nullable(),
    actions: z.array(CardActionSchema),
});
export const PaymentResultCardSchema = base('PAYMENT_RESULT').extend({
    paymentIntentId: PaymentIntentIdSchema,
    status: PaymentIntentStatusSchema,
    amount: MoneySchema,
    payeeName: z.string(),
    authCode: z.string().nullable(),
    reasonCode: ReasonCodeSchema.nullable(),
    message: z.string(),
    receiptUrl: z.string().nullable(),
});
export const CreditSummaryCardSchema = base('CREDIT_SUMMARY').extend({
    line: CreditLineSummarySchema,
});
export const SimulationTableCardSchema = base('SIMULATION_TABLE').extend({
    lineId: CreditLineIdSchema.nullable(),
    options: z.array(SimulationResultSchema).min(1),
});
export const DocumentRequestCardSchema = base('DOCUMENT_REQUEST').extend({
    applicationId: ApplicationIdSchema,
    expectedType: EvidenceTypeSchema,
    reason: z.string(),
    tips: z.array(z.string()),
    actions: z.array(CardActionSchema),
});
export const DocumentReviewCardSchema = base('DOCUMENT_REVIEW').extend({
    documentId: z.string(),
    review: DocumentReviewSchema,
});
export const ChecklistProgressCardSchema = base('CHECKLIST_PROGRESS').extend({
    applicationId: ApplicationIdSchema,
    phase: ApplicationPhaseSchema,
    completeness: z.string(),
    items: z.array(z.object({ label: z.string(), done: z.boolean() })),
});
export const CashFlowSummaryCardSchema = base('CASH_FLOW_SUMMARY').extend({
    applicationId: ApplicationIdSchema,
    analysis: CashFlowAnalysisSchema,
    actions: z.array(CardActionSchema),
});
export const OfferCardSchema = base('OFFER').extend({
    offer: OfferSchema,
    rateEA: RateSchema,
    actions: z.array(CardActionSchema),
});
export const HumanHandoffCardSchema = base('HUMAN_HANDOFF').extend({
    reason: z.string(),
    expectedWait: z.string().nullable(),
});
export const AgentCardSchema = z
    .discriminatedUnion('type', [
    PayeeConfirmationCardSchema,
    PaymentDraftCardSchema,
    PaymentResultCardSchema,
    CreditSummaryCardSchema,
    SimulationTableCardSchema,
    DocumentRequestCardSchema,
    DocumentReviewCardSchema,
    ChecklistProgressCardSchema,
    CashFlowSummaryCardSchema,
    OfferCardSchema,
    HumanHandoffCardSchema,
])
    .meta({ id: 'AgentCard' });
//# sourceMappingURL=cards.js.map